// =============================================
// AdminSidebarLayout — shared sidebar for Admin + SysAdmin
// Kipikid - מערכת נוכחות ילדים בגנים
// =============================================

import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogOut, Menu, X, HelpCircle, type LucideIcon } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useSimulationStore } from '../stores/simulationStore';
import { useAdminStore } from '../stores/adminStore';
import OrganizationFilter from '../components/common/OrganizationFilter';
import { useHelpGuide } from '../hooks/useHelpGuide';
import HelpGuideModal from '../components/common/HelpGuideModal';

const sharedColors = {
  text: '#1f2937',
  textLight: '#6b7280',
  border: '#e5e7eb',
  background: '#f9fafb',
};

export interface NavItem {
  path: string;
  icon: LucideIcon;
  label: string;
}

interface AdminSidebarLayoutProps {
  navItems: NavItem[];
  title: string;
  subtitle: string;
  primaryColor: string;
  primaryLight: string;
  loginPath: string;
  allowedRoles: string[];
  showOrgFilter?: boolean;
  mobileTitle?: React.ReactNode;
  sidebarClass?: string;
  mainClass?: string;
  cssPrefix?: string;
}

export function AdminSidebarLayout({
  navItems,
  title,
  subtitle,
  primaryColor,
  primaryLight,
  loginPath,
  allowedRoles,
  showOrgFilter = false,
  mobileTitle,
  sidebarClass = 'sidebar-desktop',
  mainClass = 'main-content-desktop',
  cssPrefix = 'md',
}: AdminSidebarLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const { logout, user, isAuthenticated } = useAuthStore();
  const { clearOrganizationFilter } = useAdminStore();
  const simActive = useSimulationStore(s => s.active);
  const helpGuide = useHelpGuide();

  // Route guard
  useEffect(() => {
    if (!isAuthenticated) {
      navigate(loginPath, { replace: true });
      return;
    }
    if (user && !allowedRoles.includes(user.role)) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, user, navigate, loginPath, allowedRoles]);

  const handleLogout = () => {
    clearOrganizationFilter();
    logout();
    navigate('/');
  };

  const hiddenClass = `${cssPrefix}-hidden`;

  return (
    <div dir="rtl" style={{
      minHeight: '100vh',
      backgroundColor: sharedColors.background,
      display: 'flex',
      paddingTop: simActive ? '36px' : 0,
    }}>
      {/* Mobile Header */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '60px',
          backgroundColor: 'white',
          borderBottom: `1px solid ${sharedColors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
          zIndex: 40,
        }}
        className={hiddenClass}
      >
        <button
          onClick={() => setSidebarOpen(true)}
          style={{ padding: '8px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}
        >
          <Menu size={24} color={sharedColors.text} />
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {mobileTitle ?? (
            <h1 style={{ fontSize: '18px', fontWeight: 'bold', color: primaryColor }}>
              {title}
            </h1>
          )}
        </div>
        <button
          onClick={helpGuide.open}
          style={{ padding: '8px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}
          title="מדריך עזרה (Ctrl+/)"
        >
          <HelpCircle size={22} color={sharedColors.textLight} />
        </button>
      </div>

      {/* Sidebar Overlay (Mobile) */}
      {sidebarOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={() => setSidebarOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 45,
          }}
          className={hiddenClass}
        />
      )}

      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{ x: sidebarOpen ? 0 : '-100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '260px',
          backgroundColor: 'white',
          borderLeft: `1px solid ${sharedColors.border}`,
          zIndex: 50,
          display: 'flex',
          flexDirection: 'column',
          transform: 'translateX(-100%)',
        }}
        className={sidebarClass}
      >
        {/* Sidebar Header */}
        <div style={{
          padding: '20px',
          borderBottom: `1px solid ${sharedColors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img
              src="/kipikid-logo.png"
              alt="KipiKid"
              style={{ width: '52px', height: '52px', objectFit: 'contain' }}
            />
            <div>
              <h2 style={{ fontSize: '16px', fontWeight: 'bold', color: primaryColor }}>
                KipiKid
              </h2>
              <p style={{ fontSize: '12px', color: sharedColors.textLight }}>{subtitle}</p>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            style={{ padding: '8px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}
            className={hiddenClass}
          >
            <X size={20} color={sharedColors.textLight} />
          </button>
        </div>

        {/* Organization Filter — shown when prop is set */}
        {showOrgFilter && (
          <div style={{ padding: '12px 0 4px', borderBottom: `1px solid ${sharedColors.border}` }}>
            <OrganizationFilter />
          </div>
        )}

        {/* Navigation */}
        <nav style={{ flex: 1, padding: '16px 12px', overflowY: 'auto' }}>
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                borderRadius: '10px',
                marginBottom: '4px',
                textDecoration: 'none',
                backgroundColor: isActive ? primaryLight : 'transparent',
                color: isActive ? primaryColor : sharedColors.text,
                fontWeight: isActive ? '600' : '500',
                fontSize: '14px',
                transition: 'all 0.2s',
              })}
            >
              <item.icon size={20} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div style={{ padding: '16px', borderTop: `1px solid ${sharedColors.border}` }}>
          {user && (
            <div style={{
              marginBottom: '12px',
              padding: '12px',
              backgroundColor: sharedColors.background,
              borderRadius: '10px',
            }}>
              <p style={{ fontSize: '14px', fontWeight: '500', color: sharedColors.text }}>{user.name}</p>
              <p style={{ fontSize: '12px', color: sharedColors.textLight }}>
                {user.organizationName || user.email}
              </p>
            </div>
          )}
          <button
            onClick={helpGuide.open}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '10px',
              backgroundColor: '#e0f2fe',
              color: '#0369a1',
              border: 'none',
              borderRadius: '10px',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              marginBottom: '8px',
            }}
          >
            <HelpCircle size={16} />
            מדריך עזרה (Ctrl+/)
          </button>
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '12px',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              border: 'none',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            <LogOut size={18} />
            התנתקות
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <main
        style={{
          flex: 1,
          marginRight: '0',
          paddingTop: '76px',
          padding: '76px 16px 16px',
        }}
        className={mainClass}
      >
        <Outlet />
      </main>

      <HelpGuideModal
        isOpen={helpGuide.isOpen}
        onClose={helpGuide.close}
        currentPath={helpGuide.currentPath}
        userRole={helpGuide.userRole}
      />

      {/* Desktop Styles */}
      <style>{`
        @media (min-width: 768px) {
          .${hiddenClass} {
            display: none !important;
          }
          .${sidebarClass} {
            transform: translateX(0) !important;
          }
          .${mainClass} {
            margin-right: 260px !important;
            padding-top: 16px !important;
          }
        }
      `}</style>
    </div>
  );
}
